import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../hooks/useLocalization';

interface LocationOption {
    id: string;
    name: string;
    type?: number;
    parentId?: string;
}

interface TourSearchQuery {
    _id: string;
    name: string;
    description?: string;
    departure: { id: string; name: string; code?: string };
    arrival: { id: string; name: string; code?: string };
    defaultNights: number;
    defaultAdults: number;
    defaultChildren: number;
    currency: string;
    schedule: {
        enabled: boolean;
        frequency: 'manual' | 'hourly' | 'daily' | 'weekly' | 'custom';
        hour: number;
        minute: number;
    };
    cacheSettings: {
        daysAhead: number;
        expirationHours: number;
    };
    status: 'active' | 'paused' | 'error' | 'running';
    lastRun?: string;
    lastRunResult?: {
        success: boolean;
        hotelsCount: number;
        offersCount: number;
        error?: string;
    };
    nextRun?: string;
    totalRuns: number;
    successfulRuns: number;
}

interface CacheStats {
    queries: { total: number; active: number; scheduled: number };
    cache: { total: number; fresh: number; stale: number; expired: number; totalHits: number };
    flights: number;
    enrichments: { total: number; active: number };
    isRunning: boolean;
}

interface SystemBackup {
    id: string;
    type: string;
    description: string;
    createdAt: string;
    size: number;
    itemsCount: number;
    status: string;
    counts: { queries: number; caches: number; flights: number; roomEnrichments: number };
}

interface CachedData {
    id: string;
    queryName: string;
    route: string;
    searchParams: any;
    hotelsCount: number;
    offersCount: number;
    status: string;
    cachedAt: string;
    expiresAt: string;
    hitCount: number;
}

interface LiveLog {
    status: string;
    message: string;
    progress: number | null;
    hotelsFound: number;
    offersFound: number;
    currentDate: string | null;
    isRunning: boolean;
    timestamp: string;
}

interface AutoFetchProgress {
    status: 'idle' | 'running' | 'stopping' | 'completed' | 'error';
    currentDestination: string | null;
    currentDate: string | null;
    totalDestinations: number;
    processedDestinations: number;
    totalDates: number;
    processedDates: number;
    hotelsFound: number;
    offersFound: number;
    errors: number;
    startedAt: string | null;
    completedAt: string | null;
    estimatedRemaining: string | null;
}

const API_BASE = '/api/tour-cache';

const TourCacheManager: React.FC = () => {
    const { t, language } = useLocalization();

    // Core state
    const [activeTab, setActiveTab] = useState<'queries' | 'data' | 'stats' | 'backups' | 'logs' | 'search' | 'auto-fetch'>('queries');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Stats state
    const [stats, setStats] = useState<CacheStats | null>({
        queries: { total: 0, active: 0, scheduled: 0 },
        cache: { total: 0, fresh: 0, stale: 0, expired: 0, totalHits: 0 },
        flights: 0,
        enrichments: { total: 0, active: 0 },
        isRunning: false
    });

    // Search state
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Queries state
    const [queries, setQueries] = useState<TourSearchQuery[]>([]);
    const [showQueryForm, setShowQueryForm] = useState(false);
    const [queryForm, setQueryForm] = useState({
        name: '',
        departure: { id: '', name: '' },
        arrival: { id: '', name: '' },
        defaultNights: 7,
        defaultAdults: 2,
        defaultChildren: 0,
        checkInStart: '',
        checkInEnd: '',
        cacheSettings: { daysAhead: 30, expirationHours: 24 },
        schedule: { enabled: false, hour: 3, minute: 0, frequency: 'daily' as const }
    });

    // Location options state
    const [departureOptions, setDepartureOptions] = useState<LocationOption[]>([]);
    const [arrivalOptions, setArrivalOptions] = useState<LocationOption[]>([]);
    const [loadingDepartures, setLoadingDepartures] = useState(false);
    const [loadingArrivals, setLoadingArrivals] = useState(false);

    // Data state
    const [dataPagination, setDataPagination] = useState({ total: 0, page: 1, limit: 20 });
    const [backups, setBackups] = useState<SystemBackup[]>([]);
    const [logHistory, setLogHistory] = useState<{ time: string; message: string; status: string }[]>([]);

    // Auto-fetch state
    const [autoFetchProgress, setAutoFetchProgress] = useState<AutoFetchProgress>({
        status: 'idle', currentDestination: null, currentDate: null,
        totalDestinations: 0, processedDestinations: 0, totalDates: 0, processedDates: 0,
        hotelsFound: 0, offersFound: 0, errors: 0,
        startedAt: null, completedAt: null, estimatedRemaining: null
    });
    const [autoFetchConfig, setAutoFetchConfig] = useState({
        departureId: '71', departureName: 'Stockholm', nights: 7, monthsAhead: 6, adults: 2, currency: 'SEK'
    });
    const [autoFetchLoading, setAutoFetchLoading] = useState(false);

    // Helper functions
    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 5000);
    };

    const formatDate = (date?: string) => {
        if (!date) return 'Never';
        return new Date(date).toLocaleString();
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
            case 'running': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 animate-pulse';
            case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
            case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    // API calls
    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/stats`);
            if (res.ok) {
                const data = await res.json();
                if (data.success && data.stats) {
                    setStats(data.stats);
                }
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    }, []);

    const fetchQueries = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/queries`);
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setQueries(data.queries || []);
                }
            }
        } catch (err) {
            console.error('Failed to fetch queries:', err);
        }
    }, []);

    const handleCreateQuery = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/queries`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(queryForm)
            });
            const data = await res.json();
            if (data.success) {
                showMessage('success', 'Query created successfully');
                setShowQueryForm(false);
                fetchQueries();
            } else {
                showMessage('error', data.error || 'Failed to create query');
            }
        } catch (err) {
            showMessage('error', 'Network error');
        }
        setLoading(false);
    };

    const handleRunQuery = async (queryId: string) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/run/${queryId}`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                showMessage('success', 'Query started');
                fetchQueries();
                fetchStats();
            } else {
                showMessage('error', data.error || 'Failed to run query');
            }
        } catch (err) {
            showMessage('error', 'Network error');
        }
        setLoading(false);
    };

    const handleDeleteQuery = async (queryId: string) => {
        if (!confirm('Are you sure you want to delete this query?')) return;
        try {
            const res = await fetch(`${API_BASE}/queries/${queryId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                showMessage('success', 'Query deleted');
                fetchQueries();
            }
        } catch (err) {
            showMessage('error', 'Failed to delete query');
        }
    };

    const handleCancelCache = async () => {
        try {
            const res = await fetch(`${API_BASE}/cancel`, { method: 'POST' });
            if (res.ok) {
                showMessage('success', 'Cache process cancelled');
                fetchStats();
            }
        } catch (err) {
            showMessage('error', 'Failed to cancel');
        }
    };

    // Fetch departures on mount
    useEffect(() => {
        const fetchDepartures = async () => {
            setLoadingDepartures(true);
            try {
                // Fixed: Use correct tour-cache endpoint
                const res = await fetch(`${API_BASE}/locations/departures`);
                if (res.ok) {
                    const data = await res.json();
                    setDepartureOptions(data.locations || []);
                }
            } catch (err) {
                console.error('Failed to fetch departures');
            }
            setLoadingDepartures(false);
        };
        fetchDepartures();
        fetchStats();
        fetchQueries();
    }, [fetchStats, fetchQueries]);

    // Fetch arrivals when departure changes
    useEffect(() => {
        if (!queryForm.departure.id) return;
        const fetchArrivals = async () => {
            setLoadingArrivals(true);
            try {
                // Fixed: Use correct tour-cache endpoint
                const res = await fetch(`${API_BASE}/locations/arrivals/${queryForm.departure.id}`);
                if (res.ok) {
                    const data = await res.json();
                    setArrivalOptions(data.locations || []);
                }
            } catch (err) {
                console.error('Failed to fetch arrivals');
            }
            setLoadingArrivals(false);
        };
        fetchArrivals();
    }, [queryForm.departure.id]);

    // Check URL params on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const hotelName = params.get('hotel');
        if (hotelName) {
            setActiveTab('search');
            handleSearchInCache(hotelName);
        }
    }, []);

    const handleSearchInCache = async (hotelName: string) => {
        setIsSearching(true);
        try {
            const res = await fetch(`${API_BASE}/search-in-cache?hotelName=${encodeURIComponent(hotelName)}`);
            const data = await res.json();
            if (data.success) {
                setSearchResults(data.results);
                if (data.results.length === 0) {
                    showMessage('error', t('no_cache_results') || 'No cached data found for this hotel');
                }
            }
        } catch (err) {
            console.error('Search error:', err);
        }
        setIsSearching(false);
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    🚀 {t('tour_cache_manager')}
                </h2>
                {stats?.isRunning && (
                    <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold animate-pulse">
                            ⏳ {t('processing')}
                        </span>
                        <button
                            onClick={handleCancelCache}
                            className="px-3 py-1.5 bg-red-500 text-white rounded-full text-sm font-bold hover:bg-red-600 active:scale-95 transition-transform"
                        >
                            ⏹️ {t('stop')}
                        </button>
                    </div>
                )}
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto pb-1">
                {[
                    { id: 'queries', label: t('queries'), count: queries.length },
                    { id: 'auto-fetch', label: '🌍 Auto-Fetch' },
                    { id: 'data', label: t('data'), count: dataPagination.total },
                    { id: 'stats', label: t('statistics') },
                    { id: 'backups', label: t('backups'), count: backups.length },
                    { id: 'logs', label: t('logs') },
                    { id: 'search', label: t('search_results'), count: searchResults.length, hidden: activeTab !== 'search' }
                ].filter(t => !t.hidden).map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`px-4 py-2 text-sm font-bold transition-colors whitespace-nowrap border-b-2 ${activeTab === tab.id
                            ? 'text-primary border-primary'
                            : 'text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        {tab.label}
                        {tab.count !== undefined && (
                            <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs">
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Search Tab */}
            {activeTab === 'search' && (
                <div>
                    <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800 mb-4 flex justify-between items-center">
                        <h3 className="font-bold text-blue-900 dark:text-blue-100">
                            Search Results for: <span className="text-blue-600">"{new URLSearchParams(window.location.search).get('hotel')}"</span>
                        </h3>
                        <button onClick={() => window.location.href = '/admin/cache'} className="text-sm text-blue-600 hover:underline">Clear Search</button>
                    </div>

                    {isSearching ? (
                        <div className="text-center py-12"><div className="animate-spin inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
                    ) : (
                        <div className="space-y-3">
                            {searchResults.map((result: any, idx) => (
                                <div key={idx} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl flex justify-between items-center">
                                    <div>
                                        <div className="font-bold">{result.queryName}</div>
                                        <div className="text-sm text-gray-500">Cached: {new Date(result.cachedAt).toLocaleString()}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold text-green-600">{result.price} {result.currency}</div>
                                        <div className="text-xs text-gray-400">Lowest Price</div>
                                    </div>
                                </div>
                            ))}
                            {searchResults.length === 0 && <div className="text-center text-gray-500 py-8">No results found in cache.</div>}
                        </div>
                    )}
                </div>
            )}

            {/* Auto-Fetch Tab */}
            {activeTab === 'auto-fetch' && (
                <AutoFetchPanel
                    progress={autoFetchProgress}
                    config={autoFetchConfig}
                    setConfig={setAutoFetchConfig}
                    loading={autoFetchLoading}
                    departureOptions={departureOptions}
                    onStart={async () => {
                        setAutoFetchLoading(true);
                        try {
                            const res = await fetch(`${API_BASE}/auto-fetch/start`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(autoFetchConfig)
                            });
                            const data = await res.json();
                            if (data.success) {
                                showMessage('success', 'Auto-fetch started');
                                setAutoFetchProgress(data.progress || { ...autoFetchProgress, status: 'running' });
                            } else {
                                showMessage('error', data.error || 'Failed to start');
                            }
                        } catch { showMessage('error', 'Network error'); }
                        setAutoFetchLoading(false);
                    }}
                    onStop={async () => {
                        try {
                            const res = await fetch(`${API_BASE}/auto-fetch/stop`, { method: 'POST' });
                            const data = await res.json();
                            if (data.success) {
                                showMessage('success', 'Stop requested');
                                setAutoFetchProgress(data.progress || { ...autoFetchProgress, status: 'stopping' });
                            }
                        } catch { showMessage('error', 'Network error'); }
                    }}
                    onRefreshStatus={async () => {
                        try {
                            const res = await fetch(`${API_BASE}/auto-fetch/status`);
                            const data = await res.json();
                            if (data.success) setAutoFetchProgress(data);
                        } catch { /* silent */ }
                    }}
                    t={t}
                />
            )}

            {/* Queries Tab */}
            {activeTab === 'queries' && (
                <div>
                    <div className="flex justify-between mb-4">
                        <button
                            onClick={() => setShowQueryForm(!showQueryForm)}
                            className="px-4 py-2 bg-primary text-white rounded-lg font-bold hover:bg-teal-700 transition-colors shadow-lg shadow-primary/20"
                        >
                            + {t('new_query')}
                        </button>
                        <button
                            onClick={() => fetchQueries()}
                            className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            🔄 {t('refresh')}
                        </button>
                    </div>

                    {/* Query Form */}
                    {showQueryForm && (
                        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 animate-fadeIn">
                            <h3 className="font-bold mb-4 text-gray-900 dark:text-white">{t('new_query')}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('query_name')}</label>
                                    <input
                                        type="text"
                                        value={queryForm.name}
                                        onChange={e => setQueryForm({ ...queryForm, name: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                        placeholder="e.g. Stockholm -> Antalya Summer"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                                        {t('departure_point')} {loadingDepartures && <span className="text-primary text-xs animate-pulse">...</span>}
                                    </label>
                                    <select
                                        value={queryForm.departure.id ? `${queryForm.departure.id}|${queryForm.departure.name}` : ''}
                                        onChange={e => {
                                            const [id, name] = e.target.value.split('|');
                                            setQueryForm({ ...queryForm, departure: { id, name } });
                                        }}
                                        disabled={loadingDepartures}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50"
                                    >
                                        <option value="">-- {t('choose')} --</option>
                                        {departureOptions.map(loc => (
                                            <option key={loc.id} value={`${loc.id}|${loc.name}`}>
                                                {loc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                                        {t('arrival_point')} {loadingArrivals && <span className="text-primary text-xs animate-pulse">...</span>}
                                    </label>
                                    <select
                                        value={queryForm.arrival.id ? `${queryForm.arrival.id}|${queryForm.arrival.name}` : ''}
                                        onChange={e => {
                                            const [id, name] = e.target.value.split('|');
                                            setQueryForm({ ...queryForm, arrival: { id, name } });
                                        }}
                                        disabled={!queryForm.departure.id || loadingArrivals}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50"
                                    >
                                        <option value="">-- {t('choose')} --</option>
                                        {arrivalOptions.map(loc => (
                                            <option key={loc.id} value={`${loc.id}|${loc.name}`}>
                                                {loc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('nights')}</label>
                                    <input
                                        type="number"
                                        value={queryForm.defaultNights}
                                        onChange={e => setQueryForm({ ...queryForm, defaultNights: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('check_in_start')}</label>
                                    <input
                                        type="date"
                                        value={queryForm.checkInStart}
                                        onChange={e => setQueryForm({ ...queryForm, checkInStart: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('check_in_end')}</label>
                                    <input
                                        type="date"
                                        value={queryForm.checkInEnd}
                                        onChange={e => setQueryForm({ ...queryForm, checkInEnd: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('adults')}</label>
                                    <input
                                        type="number"
                                        value={queryForm.defaultAdults}
                                        onChange={e => setQueryForm({ ...queryForm, defaultAdults: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('cache_days')}</label>
                                    <input
                                        type="number"
                                        value={queryForm.cacheSettings.daysAhead}
                                        onChange={e => setQueryForm({ ...queryForm, cacheSettings: { ...queryForm.cacheSettings, daysAhead: parseInt(e.target.value) } })}
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={queryForm.schedule.enabled}
                                            onChange={e => setQueryForm({ ...queryForm, schedule: { ...queryForm.schedule, enabled: e.target.checked } })}
                                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('automatic_schedule')}</span>
                                    </label>
                                </div>
                                {queryForm.schedule.enabled && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('work_hour')} (0-23)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={23}
                                            value={queryForm.schedule.hour}
                                            onChange={e => setQueryForm({ ...queryForm, schedule: { ...queryForm.schedule, hour: parseInt(e.target.value) } })}
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-3 mt-6 justify-end">
                                <button
                                    onClick={() => setShowQueryForm(false)}
                                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                >
                                    {t('cancel_edit')}
                                </button>
                                <button
                                    onClick={handleCreateQuery}
                                    disabled={loading || !queryForm.name || !queryForm.departure.id || !queryForm.arrival.id}
                                    className="px-6 py-2 bg-primary text-white rounded-lg font-bold hover:bg-teal-700 shadow-lg shadow-primary/25 disabled:opacity-50 disabled:shadow-none transition-all transform hover:scale-105"
                                >
                                    {loading ? t('saving') : t('save_query')}
                                </button>
                            </div>
                        </div >
                    )}

                    {/* Queries List */}
                    <div className="space-y-4">
                        {queries.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                                <span className="material-icons-round text-4xl text-gray-300 dark:text-gray-600 mb-2">playlist_add</span>
                                <p className="text-gray-500 dark:text-gray-400">{t('no_queries')}</p>
                            </div>
                        ) : queries.map(query => (
                            <div key={query._id} className="p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h4 className="text-lg font-bold text-gray-900 dark:text-white">{query.name}</h4>
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusBadge(query.status)}`}>
                                                {query.status}
                                            </span>
                                            {query.schedule.enabled && (
                                                <span className="px-2.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 rounded-full text-xs font-bold">
                                                    ⏰ {query.schedule.hour}:00
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
                                            <div className="flex items-center gap-1.5">
                                                <span className="material-icons-round text-sm">flight_takeoff</span>
                                                {query.departure.name} {query.departure.code && <span className="text-xs text-gray-400">({query.departure.code})</span>} → {query.arrival.name} {query.arrival.code && <span className="text-xs text-gray-400">({query.arrival.code})</span>}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="material-icons-round text-sm">nights_stay</span>
                                                {query.defaultNights} {t('nights')}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="material-icons-round text-sm">people</span>
                                                {query.defaultAdults} {t('adults')}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs">
                                            <div>
                                                <span className="text-gray-400 block mb-0.5">{t('last_run')}</span>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(query.lastRun)}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 block mb-0.5">{t('total_results')}</span>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">{query.totalRuns}</span>
                                            </div>
                                            {query.lastRunResult && (
                                                <div className="md:col-span-2">
                                                    <span className="text-gray-400 block mb-0.5">Result</span>
                                                    <span className={`font-medium ${query.lastRunResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                        {query.lastRunResult.success
                                                            ? `✅ ${query.lastRunResult.hotelsCount} hotels, ${query.lastRunResult.offersCount} offers`
                                                            : `❌ ${query.lastRunResult.error}`
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleRunQuery(query._id)}
                                            disabled={loading || query.status === 'running'}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-lg shadow-blue-500/20"
                                        >
                                            <span className="material-icons-round text-sm">play_arrow</span>
                                            {t('run')}
                                        </button>
                                        <button
                                            onClick={() => handleDeleteQuery(query._id)}
                                            className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                                            title={t('delete')}
                                        >
                                            <span className="material-icons-round">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div >
            )}

            {/* Stats Tab (Simplified for brevity, similar structure applied) */}
            {activeTab === 'stats' && stats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800">
                        <div className="text-3xl font-black text-blue-600 dark:text-blue-400">{stats.queries.total}</div>
                        <div className="text-sm font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wide mt-1">{t('total_queries')}</div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">{stats.queries.scheduled} {t('scheduled')}</div>
                    </div>
                    {/* Add other stats cards similarly */}
                    <div className="col-span-2 lg:col-span-4 mt-6">
                        <h4 className="font-bold text-gray-800 dark:text-white mb-4">Cache Health</h4>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                            <div className="bg-green-500 h-4" style={{ width: `${(stats.cache.fresh / stats.cache.total) * 100}%` }}></div>
                        </div>
                        <div className="flex justify-between text-xs mt-2 text-gray-500">
                            <span>Fresh: {stats.cache.fresh}</span>
                            <span>Total: {stats.cache.total}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
                <div className="bg-black text-green-400 p-4 rounded-xl font-mono text-xs h-96 overflow-y-auto shadow-inner">
                    {logHistory.length === 0 && <span className="opacity-50">No logs available...</span>}
                    {logHistory.map((log, i) => (
                        <div key={i} className="mb-1 border-b border-green-900/30 pb-1">
                            <span className="opacity-50">[{log.time}]</span> <span className={log.status === 'error' ? 'text-red-400' : ''}>{log.message}</span>
                        </div>
                    ))}
                    <div className="animate-pulse">_</div>
                </div>
            )}
        </div>
    );
};

// =============================================
// Auto-Fetch Panel Sub-Component
// =============================================
interface AutoFetchPanelProps {
    progress: AutoFetchProgress;
    config: { departureId: string; departureName: string; nights: number; monthsAhead: number; adults: number; currency: string };
    setConfig: (c: any) => void;
    loading: boolean;
    departureOptions: LocationOption[];
    onStart: () => void;
    onStop: () => void;
    onRefreshStatus: () => void;
    t: (key: string) => string;
}

const AutoFetchPanel: React.FC<AutoFetchPanelProps> = ({ progress, config, setConfig, loading, departureOptions, onStart, onStop, onRefreshStatus, t }) => {
    const isRunning = progress.status === 'running' || progress.status === 'stopping';
    const overallProgress = progress.totalDates > 0 ? Math.round((progress.processedDates / progress.totalDates) * 100) : 0;

    // Poll status every 3s when running
    useEffect(() => {
        if (!isRunning) return;
        const interval = setInterval(onRefreshStatus, 3000);
        return () => clearInterval(interval);
    }, [isRunning, onRefreshStatus]);

    // Fetch status on mount
    useEffect(() => { onRefreshStatus(); }, []);

    return (
        <div className="space-y-6">
            {/* Status Banner */}
            {isRunning && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl animate-pulse">
                    <div className="flex justify-between items-center mb-2">
                        <span className="font-bold text-blue-800 dark:text-blue-200">
                            ⏳ {progress.status === 'stopping' ? 'Stopping...' : 'Auto-Fetch Running'}
                        </span>
                        <button
                            onClick={onStop}
                            className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors"
                        >
                            ⏹️ {t('stop')}
                        </button>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3 mb-2">
                        <div className="bg-blue-600 h-3 rounded-full transition-all duration-500" style={{ width: `${overallProgress}%` }} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-blue-700 dark:text-blue-300">
                        <div>📍 {progress.currentDestination || '-'}</div>
                        <div>📅 {progress.currentDate || '-'}</div>
                        <div>🏨 {progress.hotelsFound} hotels</div>
                        <div>📦 {progress.offersFound} offers</div>
                    </div>
                    <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mt-2">
                        <span>Destinations: {progress.processedDestinations}/{progress.totalDestinations}</span>
                        <span>Dates: {progress.processedDates}/{progress.totalDates}</span>
                        <span>{overallProgress}%</span>
                    </div>
                </div>
            )}

            {/* Completed Banner */}
            {progress.status === 'completed' && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="font-bold text-green-800 dark:text-green-200 mb-2">✅ Auto-Fetch Complete</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-green-700 dark:text-green-300">
                        <div>🏨 {progress.hotelsFound} hotels</div>
                        <div>📦 {progress.offersFound} offers</div>
                        <div>⚠️ {progress.errors} errors</div>
                        <div>📍 {progress.processedDestinations} destinations</div>
                    </div>
                </div>
            )}

            {/* Error Banner */}
            {progress.status === 'error' && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="font-bold text-red-800 dark:text-red-200">❌ Auto-Fetch Error</div>
                    <div className="text-sm text-red-600 dark:text-red-400 mt-1">{progress.hotelsFound} hotels, {progress.offersFound} offers found before error</div>
                </div>
            )}

            {/* Configuration */}
            {!isRunning && (
                <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h3 className="font-bold mb-4 text-gray-900 dark:text-white">🌍 6-Month All-Destinations Fetch</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Fetches holiday package prices from Tourvisio for ALL destinations across the next 6 months.
                        Results are cached in TourSearchCache and bridged to per-hotel data.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('departure_point')}</label>
                            <select
                                value={`${config.departureId}|${config.departureName}`}
                                onChange={e => {
                                    const [id, name] = e.target.value.split('|');
                                    setConfig({ ...config, departureId: id, departureName: name });
                                }}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                            >
                                <option value="71|Stockholm">Stockholm</option>
                                {departureOptions.filter(d => d.id !== '71').map(loc => (
                                    <option key={loc.id} value={`${loc.id}|${loc.name}`}>{loc.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{t('nights')}</label>
                            <input
                                type="number" min={3} max={21}
                                value={config.nights}
                                onChange={e => setConfig({ ...config, nights: parseInt(e.target.value) || 7 })}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Months Ahead</label>
                            <input
                                type="number" min={1} max={12}
                                value={config.monthsAhead}
                                onChange={e => setConfig({ ...config, monthsAhead: parseInt(e.target.value) || 6 })}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={onStart}
                            disabled={loading}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 disabled:opacity-50 transition-all transform hover:scale-105"
                        >
                            {loading ? '⏳ Starting...' : '🚀 Start Auto-Fetch'}
                        </button>
                    </div>
                </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                    <div className="text-2xl font-black text-blue-600 dark:text-blue-400">{progress.processedDestinations}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase mt-1">Destinations Done</div>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                    <div className="text-2xl font-black text-green-600 dark:text-green-400">{progress.hotelsFound}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase mt-1">Hotels Found</div>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                    <div className="text-2xl font-black text-purple-600 dark:text-purple-400">{progress.offersFound}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase mt-1">Offers Cached</div>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                    <div className="text-2xl font-black text-red-600 dark:text-red-400">{progress.errors}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase mt-1">Errors</div>
                </div>
            </div>
        </div>
    );
};

export default TourCacheManager;
